/**
 * One formatter for "which syllabus is this student on" — the chip that reads
 * `"Sec 1 · IP – RI"`.
 *
 * What: `formatLevelStream` turns (orgLevel, examLevel, school) into the label;
 * `schoolChipLabel` picks which of a school's names to show.
 * Why: five surfaces rendered their own version of this string, and the rule
 * that decides the school suffix — **school is an IP concept**, because only IP
 * syllabus sequencing varies by school — has to live in exactly one place or it
 * will be applied to a G3 student somewhere within the month.
 * When: Update when the label's grammar changes; never fork a local copy.
 *
 * `src/`, not `shared/`: the server emits no chip strings. It sends the parts
 * (level, stream, resolved school) and the client decides how to say them.
 */
import { internalToDisplay, primaryLongDisplay } from '@/utils/levelMapping';

/**
 * A school slug resolved server-side by `resolveSchoolDisplay`. This is the
 * client mirror of that one producer — no school name is stored on any
 * document, so every payload carrying a school carries this shape.
 */
export interface SchoolDisplay {
  slug: string;
  name: string;
  /** Official abbreviation, e.g. "RI"; null for the schools without one. */
  shortName: string | null;
}

/**
 * How to render a primary level. `long` ("Primary 3") for student-facing
 * surfaces, where the compact form looked insubstantial with no stream suffix
 * beside it; `compact` ("P3") for the dense admin tables.
 */
export type PrimaryLevelStyle = 'long' | 'compact';

export interface LevelStreamParts {
  orgLevel?: string | null;
  /** The student's stream — `secondaryStream` on an enrollment. */
  examLevel?: string | null;
  /** Pass `schoolChipLabel(school)`. Ignored unless the stream is IP. */
  schoolLabel?: string | null;
  primaryStyle?: PrimaryLevelStyle;
}

/** The minimum a school has to look like to be nameable. */
interface NameableSchool {
  name?: string | null;
  shortName?: string | null;
}

const trimmed = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * The name to put on a chip: the official abbreviation when the catalog has
 * one, the full name otherwise. 174 of the 349 schools carry no MOE
 * abbreviation, so the fallback is the common case, not an edge case.
 *
 * Returns null when there is no school to name, so callers can omit the suffix
 * rather than render an empty one.
 */
export function schoolChipLabel(
  school: NameableSchool | null | undefined
): string | null {
  if (!school) return null;
  return trimmed(school.shortName) || trimmed(school.name) || null;
}

/**
 * The syllabus label. `''` when there is nothing to say — callers substitute
 * their own placeholder, since a dash means different things in a table cell
 * and a page sub-heading.
 *
 * Shapes:
 *   `Primary 3` / `P3`  — primary runs one exam track (PSLE) and no IP, so it
 *                         takes neither suffix
 *   `Sec 2 · G3`        — secondary always names its stream
 *   `Sec 1 · IP – RI`   — and, on IP only, the school whose sequence it follows
 */
export function formatLevelStream({
  orgLevel,
  examLevel,
  schoolLabel,
  primaryStyle = 'long',
}: LevelStreamParts): string {
  const org = trimmed(orgLevel);
  const exam = trimmed(examLevel);

  // Unknown org levels fall through to their raw value rather than vanishing:
  // a label that shows `secondary_9` is debuggable, a blank one is not.
  const levelLabel = org ? internalToDisplay(org) || org : '';

  const primaryLong = org ? primaryLongDisplay(org) : null;
  if (primaryLong) {
    return primaryStyle === 'compact' ? levelLabel : primaryLong;
  }

  const base = [levelLabel, exam].filter(Boolean).join(' · ');
  if (!base) return '';

  // THE rule. A G3 student at an IP school still sits the G3 sequence, so the
  // school says nothing about their syllabus and is left off.
  const school = trimmed(schoolLabel);
  if (exam !== 'IP' || !school) return base;

  return `${base} – ${school}`;
}
